import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./Resources.css";
import "./HomePage.css";
import PageHeader from "./components/PageHeader.js";

const LS_PROJECT_TO_LOAD = "zolaid_project_to_load_v1";
const LS_CURRENT_PROJECT_ID = "zolaid_current_project_id_v1";
const LS_PINNED_PROJECTS = "zolaid_pinned_projects_v1";

function loadPinnedMap() {
  try {
    const raw = localStorage.getItem(LS_PINNED_PROJECTS);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function savePinnedMap(map) {
  try {
    localStorage.setItem(LS_PINNED_PROJECTS, JSON.stringify(map && typeof map === "object" ? map : {}));
  } catch (_) {}
}

export default function Projects() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sort, setSort] = useState("updatedAt"); // updatedAt | createdAt | name
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pinnedMap, setPinnedMap] = useState(() => loadPinnedMap());
  const [versionsOpenId, setVersionsOpenId] = useState("");
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState("");
  const [currentId, setCurrentId] = useState(() => {
    try { return localStorage.getItem(LS_CURRENT_PROJECT_ID) || ""; } catch (_) { return ""; }
  });
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "";

  const togglePinned = (id) => {
    const pid = String(id || "");
    if (!pid) return;
    setPinnedMap((prev) => {
      const next = { ...(prev || {}) };
      if (next[pid]) delete next[pid];
      else next[pid] = Date.now();
      savePinnedMap(next);
      return next;
    });
  };

  const refresh = async ({ append = false } = {}) => {
    try {
      setError("");
      setLoading(true);
      const endpoint = apiUrl ? `${apiUrl}/projects/list` : "/projects/list";
      const nextOffset = append ? offset : 0;
      const resp = await axios.get(endpoint, {
        params: {
          q: String(qDebounced || "").trim(),
          sort,
          limit: 50,
          offset: nextOffset,
        },
      });
      const items = Array.isArray(resp.data?.projects) ? resp.data.projects : [];
      const total = Number(resp.data?.total || 0);
      const respOffset = Number(resp.data?.offset || nextOffset);
      const respLimit = Number(resp.data?.limit || 50);
      const merged = append ? [...projects, ...items] : items;
      setProjects(merged);
      setOffset(respOffset + respLimit);
      setHasMore(total ? (respOffset + respLimit) < total : (items.length === respLimit));
    } catch (e) {
      console.error("projects list failed", e);
      setError(e?.response?.data?.error || "Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  const openVersions = async (projectId) => {
    const id = String(projectId || "").trim();
    if (!id) return;
    try {
      setVersionsError("");
      setVersionsLoading(true);
      setVersionsOpenId(id);
      const endpoint = apiUrl ? `${apiUrl}/projects/version/list` : "/projects/version/list";
      const resp = await axios.get(endpoint, { params: { id } });
      setVersions(Array.isArray(resp.data?.versions) ? resp.data.versions : []);
    } catch (e) {
      console.error("project versions list failed", e);
      setVersionsError(e?.response?.data?.error || "Failed to load project versions.");
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const saveProjectSnapshot = async (projectId) => {
    const id = String(projectId || "").trim();
    if (!id) return;
    try {
      setVersionsError("");
      const label = window.prompt("Snapshot label:", `Snapshot ${new Date().toLocaleString()}`);
      if (!label) return;
      setVersionsLoading(true);
      const endpoint = apiUrl ? `${apiUrl}/projects/version/save` : "/projects/version/save";
      await axios.post(endpoint, { id, label });
      await openVersions(id);
      alert("Snapshot saved.");
    } catch (e) {
      console.error("project snapshot failed", e);
      setVersionsError(e?.response?.data?.error || "Failed to save snapshot.");
    } finally {
      setVersionsLoading(false);
    }
  };

  const restoreProjectSnapshot = async (projectId, versionId) => {
    const id = String(projectId || "").trim();
    const vid = String(versionId || "").trim();
    if (!id || !vid) return;
    try {
      setVersionsError("");
      const ok = window.confirm("Restore this snapshot? Current project state will be overwritten.");
      if (!ok) return;
      setVersionsLoading(true);
      const endpoint = apiUrl ? `${apiUrl}/projects/version/restore` : "/projects/version/restore";
      await axios.post(endpoint, { id, versionId: vid });
      await refresh({ append: false });
      await openVersions(id);
      alert("Snapshot restored.");
    } catch (e) {
      console.error("project restore failed", e);
      setVersionsError(e?.response?.data?.error || "Failed to restore snapshot.");
    } finally {
      setVersionsLoading(false);
    }
  };

  useEffect(() => {
    refresh({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOffset(0);
    refresh({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced, sort]);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(String(q || "")), 300);
    return () => clearTimeout(t);
  }, [q]);

  const sorted = useMemo(() => projects, [projects]);

  const tagFiltered = useMemo(() => {
    const tf = String(tagFilter || "").trim().toLowerCase();
    if (!tf) return sorted;
    return (sorted || []).filter((p) => Array.isArray(p?.tags) && p.tags.map(String).includes(tf));
  }, [sorted, tagFilter]);

  const pinnedProjects = useMemo(() => {
    const pinnedIds = new Set(Object.keys(pinnedMap || {}));
    const pinned = tagFiltered.filter((p) => pinnedIds.has(String(p.id)));
    pinned.sort((a, b) => Number(pinnedMap?.[String(b.id)] || 0) - Number(pinnedMap?.[String(a.id)] || 0));
    return pinned;
  }, [tagFiltered, pinnedMap]);

  const unpinnedProjects = useMemo(() => {
    const pinnedIds = new Set(Object.keys(pinnedMap || {}));
    return tagFiltered.filter((p) => !pinnedIds.has(String(p.id)));
  }, [tagFiltered, pinnedMap]);

  const allTags = useMemo(() => {
    const set = new Set();
    (projects || []).forEach((p) => {
      (Array.isArray(p?.tags) ? p.tags : []).forEach((t) => set.add(String(t)));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b)).slice(0, 50);
  }, [projects]);

  const editTags = async (p) => {
    try {
      setError("");
      const id = String(p?.id || "");
      if (!id) return;
      const current = Array.isArray(p?.tags) ? p.tags : [];
      const raw = window.prompt("Tags (comma-separated, lowercase a-z0-9_-):", current.join(","));
      if (raw == null) return;
      const nextTags = raw
        .split(",")
        .map((t) => String(t).trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 12);
      const endpointGet = apiUrl ? `${apiUrl}/projects/get` : "/projects/get";
      const resp = await axios.get(endpointGet, { params: { id } });
      const full = resp.data?.project;
      const websiteData = full?.websiteData;
      if (!websiteData || typeof websiteData !== "object") throw new Error("Project has no websiteData.");
      const endpointUpdate = apiUrl ? `${apiUrl}/projects/update` : "/projects/update";
      await axios.post(endpointUpdate, { id, name: full?.name, websiteData, tags: nextTags });
      await refresh({ append: false });
    } catch (e) {
      console.error("edit tags failed", e);
      setError(e?.response?.data?.error || e?.message || "Failed to update tags.");
    }
  };

  const renderVersionsPanel = (projectId) => {
    const id = String(projectId || "");
    if (!id || id !== String(versionsOpenId || "")) return null;
    return (
      <div style={{ marginTop: 10, padding: "10px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 900 }}>Project Versions</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="generate-btn" type="button" disabled={versionsLoading} onClick={() => openVersions(id)}>
              {versionsLoading ? "Loading..." : "Refresh"}
            </button>
            <button className="generate-btn" type="button" disabled={versionsLoading} onClick={() => saveProjectSnapshot(id)}>
              Snapshot
            </button>
            <button className="cancel-btn" type="button" onClick={() => { setVersionsOpenId(""); setVersions([]); setVersionsError(""); }}>
              Close
            </button>
          </div>
        </div>
        {versionsError ? <div style={{ marginTop: 8, color: "#ffb4b4", fontSize: 12 }}>{versionsError}</div> : null}
        {versions.length === 0 ? (
          <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
            No versions yet. Click Snapshot to save one.
          </div>
        ) : (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {versions.slice(0, 20).map((v) => (
              <div key={`${id}_${v.id}`} style={{ padding: "8px 10px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)" }}>
                <div style={{ fontWeight: 800, fontSize: 12 }}>{String(v.label || v.id)}</div>
                <div style={{ marginTop: 2, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                  {v.ts ? new Date(v.ts).toLocaleString() : ""} â€¢ {String(v.id || "")}
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="generate-btn" type="button" disabled={versionsLoading} onClick={() => restoreProjectSnapshot(id, v.id)}>
                    Restore
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const loadIntoBuilder = async (id) => {
    try {
      setError("");
      const endpoint = apiUrl ? `${apiUrl}/projects/get` : "/projects/get";
      const resp = await axios.get(endpoint, { params: { id } });
      const project = resp.data?.project;
      if (!project) throw new Error("Not found");
      // best-effort open tracking
      try {
        const openEndpoint = apiUrl ? `${apiUrl}/projects/open` : "/projects/open";
        axios.post(openEndpoint, { id }).catch(() => {});
      } catch (_) {}
      localStorage.setItem(LS_PROJECT_TO_LOAD, JSON.stringify(project));
      try {
        localStorage.setItem(LS_CURRENT_PROJECT_ID, String(project.id || ""));
        setCurrentId(String(project.id || ""));
      } catch (_) {}
      navigate("/chatbot");
    } catch (e) {
      console.error("load project failed", e);
      setError(e?.response?.data?.error || e?.message || "Failed to load project.");
    }
  };

  const setAsCurrent = (id) => {
    try {
      localStorage.setItem(LS_CURRENT_PROJECT_ID, String(id || ""));
      setCurrentId(String(id || ""));
    } catch (_) {}
  };

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(String(id || ""));
    } catch (_) {}
  };

  const copyShareLink = async (id) => {
    try {
      const origin = window.location.origin || "";
      const url = `${origin}/chatbot?projectId=${encodeURIComponent(String(id || ""))}`;
      await navigator.clipboard.writeText(url);
    } catch (_) {}
  };

  const openBuilderLink = (id) => {
    try {
      const origin = window.location.origin || "";
      const url = `${origin}/chatbot?projectId=${encodeURIComponent(String(id || ""))}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {}
  };

  const exportProject = async (id) => {
    try {
      setError("");
      const endpoint = apiUrl ? `${apiUrl}/projects/export` : "/projects/export";
      const resp = await axios.get(endpoint, { params: { id } });
      const project = resp.data?.project;
      if (!project) throw new Error("Not found");
      const blob = new Blob([JSON.stringify({ project }, null, 2)], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zolaid-project-${String(id)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("export project failed", e);
      setError(e?.response?.data?.error || e?.message || "Failed to export project.");
    }
  };

  const importProject = async () => {
    try {
      setError("");
      setImporting(true);
      const parsed = JSON.parse(importText || "{}");
      const project = parsed?.project && typeof parsed.project === "object" ? parsed.project : parsed;
      const endpoint = apiUrl ? `${apiUrl}/projects/import` : "/projects/import";
      await axios.post(endpoint, { project });
      setImportText("");
      await refresh();
    } catch (e) {
      console.error("import project failed", e);
      setError(e?.response?.data?.error || e?.message || "Failed to import project.");
    } finally {
      setImporting(false);
    }
  };

  const publishAsTemplate = async (p) => {
    try {
      setError("");
      const tplName = window.prompt("Template name:", String(p?.name || "Template"));
      if (!tplName) return;
      const description = window.prompt("Template description (optional):", "") || "";
      const tagsRaw = window.prompt("Tags (optional, comma-separated, lowercase like: saas,landing,agency):", "") || "";
      const author =
        window.prompt(
          "Author display name (optional):",
          (() => {
            try { return localStorage.getItem("zolaid_template_author") || ""; } catch (_) { return ""; }
          })()
        ) || "";
      try { localStorage.setItem("zolaid_template_author", author); } catch (_) {}
      const tags = tagsRaw.split(",").map((t) => String(t).trim()).filter(Boolean).slice(0, 10);

      const endpointGet = apiUrl ? `${apiUrl}/projects/get` : "/projects/get";
      const resp = await axios.get(endpointGet, { params: { id: p.id } });
      const project = resp.data?.project;
      const websiteData = project?.websiteData;
      if (!websiteData || typeof websiteData !== "object") throw new Error("Project has no websiteData.");

      const endpointPublish = apiUrl ? `${apiUrl}/market/templates/publish` : "/market/templates/publish";
      const pub = await axios.post(endpointPublish, {
        template: { name: tplName, description, tags, author, websiteData },
      });
      const id = pub.data?.id;
      const editKey = pub.data?.editKey;
      if (id && editKey) {
        try {
          const raw = localStorage.getItem("zolaid_template_edit_keys_v1");
          const parsed = raw ? JSON.parse(raw) : {};
          const map = parsed && typeof parsed === "object" ? parsed : {};
          map[String(id)] = String(editKey);
          localStorage.setItem("zolaid_template_edit_keys_v1", JSON.stringify(map));
        } catch (_) {}
      }
      if (id) {
        try {
          await navigator.clipboard.writeText(String(id));
        } catch (_) {}
        alert(`Published as community template. Share ID copied: ${id}`);
      }
    } catch (e) {
      console.error("publish as template failed", e);
      setError(e?.response?.data?.error || e?.message || "Failed to publish as template.");
    }
  };

  const deleteProject = async (id) => {
    try {
      setError("");
      const ok = window.confirm("Delete this project? This cannot be undone (prototype).");
      if (!ok) return;
      const endpoint = apiUrl ? `${apiUrl}/projects/delete` : "/projects/delete";
      await axios.post(endpoint, { id });
      if (String(id) === String(currentId)) {
        try { localStorage.removeItem(LS_CURRENT_PROJECT_ID); } catch (_) {}
        setCurrentId("");
      }
      await refresh();
    } catch (e) {
      console.error("delete project failed", e);
      setError(e?.response?.data?.error || "Failed to delete project.");
    }
  };

  const renameProject = async (p) => {
    try {
      setError("");
      const next = window.prompt("New project name:", String(p?.name || ""));
      if (!next) return;
      const endpoint = apiUrl ? `${apiUrl}/projects/rename` : "/projects/rename";
      await axios.post(endpoint, { id: p.id, name: next });
      await refresh();
    } catch (e) {
      console.error("rename project failed", e);
      setError(e?.response?.data?.error || "Failed to rename project.");
    }
  };

  const duplicateProject = async (p) => {
    try {
      setError("");
      const nextName = window.prompt("Name for duplicated project:", `${String(p?.name || "Project")} (Copy)`);
      if (!nextName) return;
      const endpoint = apiUrl ? `${apiUrl}/projects/duplicate` : "/projects/duplicate";
      await axios.post(endpoint, { id: p.id, name: nextName });
      await refresh();
    } catch (e) {
      console.error("duplicate project failed", e);
      setError(e?.response?.data?.error || "Failed to duplicate project.");
    }
  };

  return (
    <div className="resources-page">
      <PageHeader
        customNav={[
          { label: "Resources", to: "/Resources" },
          { label: "Templates", to: "/Templates" },
          { label: "Projects", to: "/Projects" },
        ]}
      />

      <div className="resources-content">
        <h1>Projects (Prototype)</h1>
        <p className="resources-subtitle">
          Persist your WebsiteBuilder work on the backend (disk-backed JSON).
        </p>

        <section style={sectionStyle}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", marginBottom: 10 }}>
            <strong>Current project</strong>: {currentId ? <code>{currentId}</code> : <span>(none)</span>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0 }}>Your projects</h2>
            <button className="generate-btn" type="button" onClick={() => refresh({ append: false })} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects (name or id)..."
              style={{
                flex: "1 1 280px",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.18)",
                color: "#ffffff",
                outline: "none",
              }}
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.18)",
                color: "#ffffff",
                outline: "none",
              }}
            >
              <option value="updatedAt">Sort: Updated</option>
              <option value="lastOpenedAt">Sort: Last opened</option>
              <option value="openCount">Sort: Most opened</option>
              <option value="createdAt">Sort: Created</option>
              <option value="name">Sort: Name</option>
            </select>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.18)",
                color: "#ffffff",
                outline: "none",
              }}
            >
              <option value="">Filter: (all tags)</option>
              {allTags.map((t) => (
                <option key={`tag_${t}`} value={t}>
                  #{t}
                </option>
              ))}
            </select>
            {hasMore ? (
              <button className="generate-btn" type="button" onClick={() => refresh({ append: true })} disabled={loading}>
                Load more
              </button>
            ) : null}
          </div>
          {error && <div style={{ marginTop: 8, color: "#ffb4b4", fontSize: 12 }}>{error}</div>}
          {sorted.length === 0 ? (
            <div style={{ marginTop: 10, color: "rgba(255,255,255,0.75)" }}>
              No projects yet. Save one from the WebsiteBuilder.
            </div>
          ) : (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
              {pinnedProjects.length > 0 ? (
                <div style={{ padding: "10px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.18)" }}>
                  <div style={{ fontWeight: 900, marginBottom: 8 }}>Pinned</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {pinnedProjects.map((p) => (
                      <div
                        key={`pinned_${p.id}`}
                        style={{
                          ...cardStyle,
                          border: String(p.id) === String(currentId) ? "1px solid rgba(124,58,237,0.55)" : cardStyle.border,
                          background: String(p.id) === String(currentId) ? "rgba(124,58,237,0.08)" : cardStyle.background,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 900 }}>{p.name || "Project"}</div>
                          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                            {p.id} â€¢ pages {Number(p.pageCount || 0)} â€¢ opened {Number(p.openCount || 0)}x{p.lastOpenedAt ? ` (last ${new Date(p.lastOpenedAt).toLocaleDateString()})` : ""} â€¢ updated {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : ""}
                          </div>
                          {Array.isArray(p.tags) && p.tags.length > 0 ? (
                            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {p.tags.slice(0, 10).map((t) => (
                                <span key={`${p.id}_tag_${t}`} style={{ padding: "3px 8px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)", fontSize: 12 }}>
                                  #{t}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <button className="generate-btn" type="button" onClick={() => togglePinned(p.id)}>
                            Unpin
                          </button>
                          <button className="generate-btn" type="button" onClick={() => editTags(p)}>
                            Tags
                          </button>
                          <button className="generate-btn" type="button" onClick={() => openVersions(p.id)}>
                            Versions
                          </button>
                          <button className="generate-btn" type="button" onClick={() => copyId(p.id)}>
                            Copy ID
                          </button>
                          <button className="generate-btn" type="button" onClick={() => copyShareLink(p.id)}>
                            Share link
                          </button>
                          <button className="generate-btn" type="button" onClick={() => exportProject(p.id)}>
                            Export
                          </button>
                          <button className="generate-btn" type="button" onClick={() => publishAsTemplate(p)}>
                            Publish as template
                          </button>
                          <button className="generate-btn" type="button" onClick={() => setAsCurrent(p.id)}>
                            Set current
                          </button>
                          <button className="generate-btn" type="button" onClick={() => renameProject(p)}>
                            Rename
                          </button>
                          <button className="generate-btn" type="button" onClick={() => duplicateProject(p)}>
                            Duplicate
                          </button>
                          <button className="generate-btn" type="button" onClick={() => loadIntoBuilder(p.id)}>
                            Load in builder
                          </button>
                          <button className="cancel-btn" type="button" onClick={() => deleteProject(p.id)}>
                            Delete
                          </button>
                        </div>
                        {renderVersionsPanel(p.id)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {unpinnedProjects.map((p) => (
                <div
                  key={p.id}
                  style={{
                    ...cardStyle,
                    border: String(p.id) === String(currentId) ? "1px solid rgba(124,58,237,0.55)" : cardStyle.border,
                    background: String(p.id) === String(currentId) ? "rgba(124,58,237,0.08)" : cardStyle.background,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900 }}>{p.name || "Project"}</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
                      {p.id} â€¢ pages {Number(p.pageCount || 0)} â€¢ opened {Number(p.openCount || 0)}x{p.lastOpenedAt ? ` (last ${new Date(p.lastOpenedAt).toLocaleDateString()})` : ""} â€¢ updated {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : ""}
                    </div>
                    {Array.isArray(p.tags) && p.tags.length > 0 ? (
                      <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {p.tags.slice(0, 10).map((t) => (
                          <span key={`${p.id}_tag_${t}`} style={{ padding: "3px 8px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.18)", fontSize: 12 }}>
                            #{t}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button className="generate-btn" type="button" onClick={() => togglePinned(p.id)}>
                      Pin
                    </button>
                    <button className="generate-btn" type="button" onClick={() => editTags(p)}>
                      Tags
                    </button>
                    <button className="generate-btn" type="button" onClick={() => openVersions(p.id)}>
                      Versions
                    </button>
                    <button className="generate-btn" type="button" onClick={() => copyId(p.id)}>
                      Copy ID
                    </button>
                    <button className="generate-btn" type="button" onClick={() => copyShareLink(p.id)}>
                      Share link
                    </button>
                    <button className="generate-btn" type="button" onClick={() => openBuilderLink(p.id)}>
                      Open link
                    </button>
                    <button className="generate-btn" type="button" onClick={() => openBuilderLink(p.id)}>
                      Open link
                    </button>
                    <button className="generate-btn" type="button" onClick={() => exportProject(p.id)}>
                      Export
                    </button>
                    <button className="generate-btn" type="button" onClick={() => publishAsTemplate(p)}>
                      Publish as template
                    </button>
                    <button className="generate-btn" type="button" onClick={() => setAsCurrent(p.id)}>
                      Set current
                    </button>
                    <button className="generate-btn" type="button" onClick={() => renameProject(p)}>
                      Rename
                    </button>
                    <button className="generate-btn" type="button" onClick={() => duplicateProject(p)}>
                      Duplicate
                    </button>
                    <button className="generate-btn" type="button" onClick={() => loadIntoBuilder(p.id)}>
                      Load in builder
                    </button>
                    <button className="cancel-btn" type="button" onClick={() => deleteProject(p.id)}>
                      Delete
                    </button>
                  </div>
                  {renderVersionsPanel(p.id)}
                </div>
              ))}
            </div>
          )}
        </section>

        <section style={sectionStyle}>
          <h2>Import project JSON</h2>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={6}
            placeholder='Paste exported JSON here ({"project": {...}})'
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(0,0,0,0.18)",
              color: "#ffffff",
              outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <button className="generate-btn" type="button" disabled={!importText.trim() || importing} onClick={importProject}>
              {importing ? "Importing..." : "Import"}
            </button>
            <button className="generate-btn" type="button" onClick={() => setImportText("")}>
              Clear
            </button>
          </div>
        </section>

        <p style={{ marginTop: 14, color: "rgba(255,255,255,0.75)" }}>
          Storage: backend file <code>uploads/projects.json</code> (prototype).
        </p>
      </div>

      <footer className="resources-footer">
        <p>Â© 2025 ZOLAID. All rights reserved.</p>
        <div className="footer-links">
          <Link to="/PrivacyPolicy">Privacy Policy</Link>
          <span>|</span>
          <Link to="/TermsOfService">Terms of Service</Link>
          <span>|</span>
          <Link to="/Contact">Contact</Link>
        </div>
      </footer>
    </div>
  );
}

const sectionStyle = {
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.10)",
  background: "rgba(255,255,255,0.04)",
  marginBottom: 12,
};

const cardStyle = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 12px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.10)",
  background: "rgba(255,255,255,0.03)",
};

